import { useEffect, useState } from "react";
import clsx from "clsx";
import { leaderboardApi } from "../api/leaderboard";
import { useGameStore } from "../store/useGameStore";
import { FormattedNumber } from "../components/FormattedNumber";
import { PlayerProfileModal } from "../components/PlayerProfileModal";
import { PlayerNameBadges } from "../components/PlayerNameBadges";
import { getHeroImagePath } from "../utils/assets";
import type { LeaderboardEntry, PlayerProfile } from "../types";

export function TopScreen() {
  const { hero, user } = useGameStore();
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [myRank, setMyRank] = useState<number | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPlayer, setSelectedPlayer] = useState<PlayerProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    leaderboardApi
      .getLeaderboard()
      .then((data) => {
        if (cancelled) return;
        setEntries(data.entries);
        setMyRank(data.my_rank);
        setTotal(data.total);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load leaderboard");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const openProfile = async (userId: number) => {
    setProfileLoading(true);
    try {
      const profile = await leaderboardApi.getPlayerProfile(userId);
      setSelectedPlayer(profile);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load player profile");
    } finally {
      setProfileLoading(false);
    }
  };

  return (
    <div className="p-4 space-y-4 pb-8">
      <h2 className="text-xl font-bold font-game text-center text-gold-400">🏆 Top Players</h2>
      <p className="text-gray-400 text-center text-sm">
        Ranked by Battle Rating (BR)
      </p>

      {hero && (
        <div className="bg-kingdom-purple border border-kingdom-glow/40 rounded-xl p-3 flex items-center justify-between">
          <div>
            <div className="text-xs text-gray-400">Your BR</div>
            <div className="text-2xl font-bold text-gold-300 tabular-nums">
              <FormattedNumber value={hero.battle_rating ?? 0} mode="exact" />
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-gray-400">Your rank</div>
            <div className="text-lg font-bold text-white">
              {myRank ? `#${myRank}` : total > 0 ? "—" : "N/A"}
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="text-center text-gray-400 py-12 animate-pulse">Loading leaderboard...</div>
      )}

      {error && (
        <div className="bg-rose-900/30 border border-rose-500 rounded-xl p-3 text-rose-300 text-sm">
          {error}
        </div>
      )}

      {!loading && !error && (
        <div className="bg-kingdom-purple border border-kingdom-mid rounded-xl overflow-hidden">
          <div className="grid grid-cols-[2.5rem_1fr_auto] gap-2 px-3 py-2 text-[10px] uppercase tracking-wide text-gray-500 border-b border-kingdom-mid">
            <span>#</span>
            <span>Player</span>
            <span className="text-right">BR</span>
          </div>

          {entries.length === 0 ? (
            <div className="p-6 text-center text-gray-500 text-sm">No players yet.</div>
          ) : (
            <div className="divide-y divide-kingdom-mid/60">
              {entries.map((entry) => (
                <button
                  key={entry.user_id}
                  type="button"
                  onClick={() => openProfile(entry.user_id)}
                  disabled={profileLoading}
                  className={clsx(
                    "w-full grid grid-cols-[2.5rem_1fr_auto] gap-2 px-3 py-2.5 text-left transition-colors",
                    entry.user_id === user?.id
                      ? "bg-kingdom-glow/10 hover:bg-kingdom-glow/15"
                      : "hover:bg-kingdom-mid/40",
                    profileLoading && "opacity-60",
                  )}
                >
                  <span className="text-sm font-bold text-gray-400 tabular-nums pt-1">
                    {entry.rank}
                  </span>
                  <div className="flex items-center gap-2 min-w-0">
                    <img
                      src={getHeroImagePath(entry.hero_class, entry.gender)}
                      alt={entry.display_name}
                      className="w-9 h-9 rounded-lg border border-kingdom-light object-cover object-top shrink-0"
                    />
                    <div className="min-w-0">
                      <PlayerNameBadges
                        name={entry.display_name}
                        guildName={entry.guild_name}
                        vipSubscriptionActive={entry.vip_subscription_active}
                        proActive={entry.pro_active}
                        size="sm"
                        nameClassName="truncate block max-w-full"
                      />
                      <div className="text-[11px] text-gray-400">
                        {entry.hero_class} · Lv.{entry.level}
                      </div>
                    </div>
                  </div>
                  <span className="text-sm font-bold text-gold-300 tabular-nums pt-1">
                    <FormattedNumber value={entry.battle_rating} mode="exact" />
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {selectedPlayer && (
        <PlayerProfileModal
          profile={selectedPlayer}
          onClose={() => setSelectedPlayer(null)}
        />
      )}
    </div>
  );
}
